import { useState, useImperativeHandle, forwardRef } from 'react';

export interface WhatsAppFloatRef {
  openModal: () => void;
}

const WhatsAppFloat = forwardRef<WhatsAppFloatRef>((props, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal: () => setIsModalOpen(true)
  }));
  const [formData, setFormData] = useState({
    nome: '',
    telefone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || ''
    };
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, telefone: formatted });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Capturar data e hora no fuso horário de São Paulo
    const now = new Date();
    const brasiliaTime = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(now);

    // Montar payload
    const payload = {
      nome: formData.nome,
      telefone: formData.telefone,
      data_hora: brasiliaTime,
      origem: 'whatsapp_float',
      ...getUTMParams()
    };

    // Tentar enviar para o webhook (não bloqueia o redirecionamento se falhar)
    try {
      await fetch('https://webhooks.manager01.m4track.com.br/webhook/Giolaser-americana', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      console.log('Dados enviados com sucesso para o webhook');
    } catch (error) {
      console.error('Erro ao enviar dados para webhook:', error);
      // Continua mesmo com erro no webhook
    }

    // Redirecionar para WhatsApp (sempre executa)
    const phoneNumber = '5519978232002';
    const message = encodeURIComponent(`Olá! Meu nome é ${formData.nome}. Vim pelo site e gostaria de agendar minhas três sessões grátis.`);
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');

    // Fechar modal e limpar formulário
    setIsModalOpen(false);
    setFormData({ nome: '', telefone: '' });
    setIsSubmitting(false);
  };

  return (
    <>
      {/* Botão Flutuante */}
      <button
        onClick={() => setIsModalOpen(true)}
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 bg-[#25D366] hover:bg-[#20BA5A] text-white w-16 h-16 sm:w-20 sm:h-20 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center group"
        aria-label="Abrir WhatsApp"
      >
        <i className="ri-whatsapp-fill text-4xl sm:text-5xl"></i>
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full animate-pulse"></div>
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full relative animate-in fade-in zoom-in duration-300">
            {/* Header */}
            <div className="bg-white rounded-t-3xl p-6 relative border-b border-gray-100">
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Fechar"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#16A34A] rounded-full flex items-center justify-center">
                  <i className="ri-whatsapp-fill text-white text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Quase lá!</h3>
                  <p className="text-sm text-gray-600">Preencha seus dados para falar no Whatsapp</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Nome */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-user-line text-gray-400"></i>
                  Nome completo
                </label>
                <input
                  type="text"
                  placeholder="Digite seu nome completo"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#16A34A] focus:outline-none transition-colors text-base"
                />
              </div>

              {/* Telefone */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-whatsapp-line text-gray-400"></i>
                  WhatsApp
                </label>
                <input
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={formData.telefone}
                  onChange={handlePhoneChange}
                  required
                  maxLength={15}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#16A34A] focus:outline-none transition-colors text-base"
                />
              </div>

              {/* Botão Enviar */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white font-bold text-base px-6 py-4 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <i className="ri-send-plane-fill text-xl"></i>
                {isSubmitting ? 'Enviando...' : 'Enviar'}
              </button>

              {/* Segurança */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <i className="ri-lock-line text-[#16A34A]"></i>
                <span>Seus dados estão seguros e protegidos</span>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
});

WhatsAppFloat.displayName = 'WhatsAppFloat';

export default WhatsAppFloat;
